import { FC } from 'react';
import styled from 'styled-components';
import CollapseOnOverflow from '../misc/collapse-on-overflow';

type InlineLinkProps = {
	customClass: boolean;
};

const InlineLink = styled.a<InlineLinkProps>`
	display: inline;
	color: rgb(var(--embed-link-color));
	${props =>
		props.customClass
			? `color: inherit;
		text-decoration: underline;`
			: ''}
`;

type GetCorrectLabelProps = {
	innerHtml: boolean;
	label: string;
	linkCallbacks: Array<() => void> | (() => void);
	linkEmbedded: boolean;
	customClass: boolean;
	splitRegex: RegExp;
	formatRegex: RegExp;
};

const getCorrectLabel = ({
	innerHtml,
	label,
	linkCallbacks,
	linkEmbedded,
	customClass,
	splitRegex,
	formatRegex
}: GetCorrectLabelProps) => {
	if (innerHtml) {
		return (
			<div
				className="innerhtml-text"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					__html: label
				}}
			/>
		);
	}

	/**
	 * formatLabel converts text wrapped in square brackets into links.
	 * e.g. "I accept the [t&c]." will become "I accept the <a>t&c</a>."

	 * @param {string} text - The text to format
	*/

	const formatLabel = (text: string) => {
		const matches = label?.match(splitRegex)?.map(m => m.replace(formatRegex, ''));
		return text?.split(splitRegex).map((part, i) => {
			const index = matches?.findIndex(m => m === part);

			if (index === undefined || index < 0) {
				return part;
			}

			return (
				<InlineLink
					onClick={e => {
						e.preventDefault();
						const callback = Array.isArray(linkCallbacks) ? linkCallbacks[index] : linkCallbacks;
						callback();
					}}
					customClass={customClass}
					// Array is static so it's okay to use index as key here
					// eslint-disable-next-line react/no-array-index-key
					key={`${part} ${i}`}
				>
					{part}
				</InlineLink>
			);
		});
	};

	return <div className="innerhtml-text">{linkEmbedded ? formatLabel(label) : label}</div>;
};

type Props = {
	id: string;
	label: string;
	isChecked: boolean;
	disabled?: boolean;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	validationMessage: string;
	iconPosition: string;
	innerHtml?: boolean;
	collapseOnOverflow?: boolean;
	showMoreLabel?: string;
	showLessLabel?: string;
	lightBg?: boolean;
	dataAt: string;
	linkCallbacks?: Array<() => void> | (() => void);
	linkEmbedded?: boolean;
	overflowHeight?: string;
	className?: string;
	customClass?: boolean;
	splitRegex?: RegExp;
	formatRegex?: RegExp;
};

const CheckBox: FC<Props> = ({
	id,
	label,
	isChecked,
	disabled = false,
	onChange,
	validationMessage,
	iconPosition,
	innerHtml = false,
	collapseOnOverflow = false,
	showMoreLabel,
	showLessLabel,
	lightBg = false,
	dataAt = '',
	linkCallbacks,
	linkEmbedded,
	overflowHeight,
	className,
	customClass = false,
	splitRegex = /\[(.*?)\]/g,
	formatRegex = /[[\]]+/g
}) => (
	<fieldset
		disabled={disabled}
		className={`${className || ''} custom custom--checkbox ${lightBg ? 'light-bg' : ''}`.trim()}
	>
		<input
			type="checkbox"
			className="custom__input"
			onChange={onChange}
			id={id}
			name={id}
			data-at={dataAt}
			checked={isChecked}
			autoCapitalize="none"
		/>
		<label
			htmlFor={id}
			className={`custom__label custom-checkbox__${iconPosition || ''} u-pr3 ${validationMessage ? 'error' : ''}`}
		>
			{collapseOnOverflow ? (
				<CollapseOnOverflow
					showMoreLabel={showMoreLabel || ''}
					showLessLabel={showLessLabel || ''}
					overflowHeight={overflowHeight || ''}
				>
					{getCorrectLabel({
						innerHtml,
						label,
						linkCallbacks,
						linkEmbedded,
						customClass,
						splitRegex,
						formatRegex
					} as GetCorrectLabelProps)}
				</CollapseOnOverflow>
			) : (
				getCorrectLabel({
					innerHtml,
					label,
					linkCallbacks,
					linkEmbedded,
					customClass,
					splitRegex,
					formatRegex
				} as GetCorrectLabelProps)
			)}
		</label>
		{validationMessage && (
			<span className="input-wrap__error-display js-input-error-display">{validationMessage}</span>
		)}
	</fieldset>
);

export default CheckBox;
